import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  canPromptNative,
  isInAppBrowser,
  isIos,
  isStandalone,
  promptNativeInstall,
  realBrowserName,
  subscribe,
} from '../lib/pwaInstall'
import IosInstallSteps from '../components/IosInstallSteps'
import CopyLinkButton, { APP_URL } from '../components/CopyLinkButton'

// /install — a PUBLIC page (no <Guard>) Sébastien can text to anyone,
// customer or not. It shows ONLY what applies to the device reading it, so
// nobody is ever told to tap a button their browser does not have.
//
// Five contexts: already installed · in-app browser · iOS · Android/Chrome ·
// desktop.
export default function InstallPage() {
  const [, force] = useState(0)
  useEffect(() => subscribe(() => force((n) => n + 1)), [])

  const installed = isStandalone()
  const inApp = isInAppBrowser()
  const ios = isIos()
  const native = canPromptNative()
  // No touch and no native prompt ⇒ a computer. Phones either report iOS or
  // offer beforeinstallprompt.
  const desktop = !ios && !native && !inApp
    && typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) < 1

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <div className="mx-auto max-w-md px-5 pb-16 pt-10">
        <header className="text-center">
          <img
            src="/logo-ghost.png"
            alt="Ghost Laundry"
            width="72"
            height="72"
            className="mx-auto"
            style={{ height: 72, width: 72 }}
          />
          <h1 className="mt-4 font-bricolage text-[24px] font-extrabold text-navy">
            {/* Plain strings, not JSX text — an &rsquo; entity would print
                literally here. */}
            {installed ? 'You’re all set' : 'Get the Ghost Laundry app'}
          </h1>
          {!installed && (
            <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
              Book a pickup in one tap, straight from your home screen.
            </p>
          )}
        </header>

        <div className="mt-7">
          {installed ? <AlreadyInstalled />
            : inApp ? <InAppBrowser />
            : ios ? <IosPath />
            : desktop ? <Desktop />
            : <AndroidPath native={native} />}
        </div>

        <Link
          to="/"
          className="mt-8 block w-full rounded-xl border-[1.5px] border-navy bg-white py-3 text-center font-bricolage text-[13px] font-extrabold text-navy"
        >
          Open Ghost Laundry
        </Link>
      </div>
    </div>
  )
}

function Card({ children }) {
  return <div className="rounded-2xl border border-line bg-white p-5 shadow-sm">{children}</div>
}

function Title({ children }) {
  return <div className="font-bricolage text-[16px] font-extrabold text-navy">{children}</div>
}

function AlreadyInstalled() {
  return (
    <Card>
      <div className="text-center">
        <div className="text-[34px] leading-none" aria-hidden>✅</div>
        <Title>It&rsquo;s already done</Title>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
          You&rsquo;re reading this from the installed app. Nothing else to do.
        </p>
      </div>
    </Card>
  )
}

// The case that failed in front of a real customer: Instagram, WhatsApp,
// Gmail & co open links in their own browser, whose Share sheet has no
// "Add to Home Screen". No install button here — just the way out.
function InAppBrowser() {
  const browser = realBrowserName()
  return (
    <Card>
      <Title>📱 Open this page in {browser} first</Title>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
        You opened this link inside another app. That browser can&rsquo;t add
        apps to your home screen — {browser} can.
      </p>
      <ol className="mt-4 space-y-3">
        <li className="flex items-center gap-3 rounded-xl bg-[#F9FAFB] p-3">
          <Num>1</Num>
          <span className="flex-1 text-[13px] text-navy">Copy the link below</span>
        </li>
        <li className="flex items-center gap-3 rounded-xl bg-[#F9FAFB] p-3">
          <Num>2</Num>
          <span className="flex-1 text-[13px] text-navy">
            Open <b>{browser}</b> and paste it in the address bar
          </span>
        </li>
        <li className="flex items-center gap-3 rounded-xl bg-[#F9FAFB] p-3">
          <Num>3</Num>
          <span className="flex-1 text-[13px] text-navy">
            Come back to this page — it will show you the steps
          </span>
        </li>
      </ol>
      <div className="mt-4">
        <CopyLinkButton
          className="w-full rounded-xl bg-navy py-3 text-center font-bricolage text-[13px] font-extrabold text-white"
          label={`Copy link for ${browser}`}
        />
      </div>
      <p className="mt-2 break-all text-center text-[11px] text-ink-3">{APP_URL}</p>
    </Card>
  )
}

function IosPath() {
  return (
    <Card>
      <Title>Add to your home screen</Title>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
        Three taps in Safari and the Ghost icon is on your home screen.
      </p>
      <div className="mt-4">
        <IosInstallSteps />
      </div>
      <div className="mt-3 text-center text-[20px] text-aqua" aria-hidden>↓</div>
      <p className="text-center text-[11px] text-ink-3">
        The Share button is in Safari&apos;s bottom bar
      </p>
    </Card>
  )
}

function AndroidPath({ native }) {
  const [state, setState] = useState('idle') // idle | done | unavailable

  async function install() {
    const outcome = await promptNativeInstall()
    setState(outcome === 'accepted' ? 'done' : outcome === 'unavailable' ? 'unavailable' : 'idle')
  }

  if (state === 'done') return <AlreadyInstalled />

  return (
    <Card>
      <Title>Install Ghost Laundry</Title>
      {native ? (
        <>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
            One tap — your phone handles the rest.
          </p>
          <button
            type="button"
            onClick={install}
            className="mt-4 w-full rounded-xl bg-navy py-3 font-bricolage text-[14px] font-extrabold text-white"
          >
            Install
          </button>
        </>
      ) : (
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
          Your browser didn&rsquo;t offer the one-tap install. Use the menu
          instead — it takes two taps.
        </p>
      )}

      {/* Always shown: the native prompt can be unavailable because the app
          is already installed, or because Chrome has not armed it yet. */}
      <ol className="mt-4 space-y-3">
        <li className="flex items-center gap-3 rounded-xl bg-[#F9FAFB] p-3">
          <Num>1</Num>
          <span className="flex-1 text-[13px] text-navy">
            Tap the <b>⋮</b> menu, top right of Chrome
          </span>
        </li>
        <li className="flex items-center gap-3 rounded-xl bg-[#F9FAFB] p-3">
          <Num>2</Num>
          <span className="flex-1 text-[13px] text-navy">
            Tap <b>Add to Home screen</b>, then <b>Install</b>
          </span>
        </li>
      </ol>
    </Card>
  )
}

function Desktop() {
  return (
    <Card>
      <Title>💻 This one lives on your phone</Title>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
        Ghost Laundry installs on a phone, not a computer. Send yourself this
        link and open it there.
      </p>
      <div className="mt-4">
        <CopyLinkButton
          className="w-full rounded-xl bg-navy py-3 text-center font-bricolage text-[13px] font-extrabold text-white"
          label="Copy the link"
        />
      </div>
      <p className="mt-2 break-all text-center text-[11px] text-ink-3">{APP_URL}</p>
      <p className="mt-3 text-center text-[12px] text-ink-2">
        You can keep booking right here in the meantime.
      </p>
    </Card>
  )
}

function Num({ children }) {
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-[12px] font-extrabold text-white">
      {children}
    </span>
  )
}
